"use client";

import { Button } from "@nudj-digital/design-system/components/ui/button";
import { Input } from "@nudj-digital/design-system/components/ui/input";
import { Label } from "@nudj-digital/design-system/components/ui/label";

import { cn } from "@nudj-digital/design-system/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import Cookie from "js-cookie";
import {
  ArrowRight,
  Building2,
  ChevronDown,
  Mail,
  Search,
  Sparkles,
} from "lucide-react";
import nextDynamic from "next/dynamic";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { signIn, signOut, useSession } from "next-auth/react";
import { Suspense, useCallback, useEffect, useState } from "react";
import { getOrganisationOptionsForAdmin } from "@/auth/db/actions";
import { AuthScreen } from "@/components/auth/auth-modal";
import {
  useSafePathname,
  useSafeSearchParams,
} from "@/hooks/use-safe-navigation";
import { setCookie } from "@/lib/actions";
import { logger } from "@/lib/logger";

// Dynamically import Three.js particle component with no SSR
const ThreeParticles = nextDynamic(
  () => import("@/components/ui/three-particles"),
  {
    ssr: false,
    loading: () => (
      <div className="fixed inset-0 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20" />
    ),
  }
);

// Content component that uses searchParams
function WelcomePageContent() {
  const router = useRouter();
  const pathname = useSafePathname();
  const searchParams = useSafeSearchParams();
  const session = useSession();
  const [mounted, setMounted] = useState(false);

  // Forcefully refresh session to get latest data before page loads
  const handleRefreshSession = useCallback(async () => {
    try {
      // Call server action to get fresh session data
      await session?.update({ forceFreshData: true });
    } catch (error) {
      logger.error("Session refresh error:", {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, [mounted]);

  // Refresh session immediately when component mounts
  useEffect(() => {
    setTimeout(() => {
      handleRefreshSession();
    }, 5000);
  }, [handleRefreshSession, router]);

  // Check if redirected due to needing organization selection
  const needsOrgSelection = searchParams.get("selectOrg") === "true";

  // Add new state for organisations
  const cookieOrganisationId = Cookie.get("organisationId");
  const selectedCommunity = Cookie.get("selectedCommunity");
  const [organisations, setOrganisations] = useState<
    {
      value: string;
      label: string;
      language: string;
      slug: string;
      communityCount: number;
    }[]
  >([]);
  const [selectedOrg, setSelectedOrg] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [orgSearchQuery, setOrgSearchQuery] = useState("");
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  // Helper function to get community object from cookie
  const getCommunityObjectFromCookie = () => {
    if (!selectedCommunity) return null;

    try {
      const parsed = JSON.parse(selectedCommunity);
      if (typeof parsed === "object" && parsed !== null && parsed.id) {
        return parsed;
      }
    } catch {
      // Not a valid JSON object
    }
    return null;
  };

  const community = getCommunityObjectFromCookie();

  // Filter organizations based on search query
  const filteredOrganisations =
    organisations?.filter((org) =>
      org.label.toLowerCase().includes(orgSearchQuery.toLowerCase())
    ) || [];

  // Load organisations when user is logged in
  useEffect(() => {
    const loadOrganisations = async () => {
      if (!session?.data?.user) return;

      setIsLoading(true);
      try {
        const orgs = await getOrganisationOptionsForAdmin();
        setOrganisations(orgs);

        // If user has no organizations, redirect to create one
        if (orgs.length === 0) {
          router.push("/create-organization");
          return;
        }

        // Auto-select the first org if we're redirected for org selection and have options
        if (needsOrgSelection && orgs.length > 0) {
          setSelectedOrg(orgs[0].value);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadOrganisations();
  }, [session?.data?.user, needsOrgSelection, router]);

  // Only show theme-aware logo after mounting to prevent hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  // Always use light logo on welcome page since background is always dark
  const logoSrc = "/nudj-logos/Nudj-large-light.png";

  const handleContinue = async () => {
    if (!selectedOrg) return;

    try {
      if (
        community?.organisationId &&
        selectedOrg !== community.organisationId.toString()
      ) {
        Cookie.remove("selectedCommunity");
      }
      const result = await setCookie("organisationId", selectedOrg);

      const orgLanguage = organisations?.find(
        (o) => o.value === selectedOrg
      )?.language;

      if (orgLanguage) {
        await setCookie("content_language", orgLanguage);
      }

      if (result && "error" in result) {
        logger.error("Failed to set organisation cookie", {
          error: result.error,
        });
        throw new Error(result.error);
      }

      // Check if there's a pending chat message
      const pendingMessage = localStorage.getItem("pendingChatMessage");
      if (pendingMessage) {
        window.location.href = "/chat";
      } else {
        // Find the selected organization to check community count
        const selectedOrgData = organisations?.find(
          (o) => o.value === selectedOrg
        );

        // If org has only 1 community, go directly to /admin (will use default community)
        // If org has multiple communities, go to org dashboard to select one
        if (selectedOrgData && selectedOrgData.communityCount === 1) {
          window.location.href = "/admin";
        } else if (selectedOrgData?.slug) {
          // Multiple communities - go to org dashboard
          window.location.href = `/${selectedOrgData.slug}`;
        } else {
          // Fallback to admin if no slug available
          window.location.href = "/admin";
        }
      }
    } catch (error) {
      logger.error("Failed to set organisation", {
        error:
          error instanceof Error
            ? { name: error.name, message: error.message, stack: error.stack }
            : { message: String(error) },
      });
    }
  };

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden"
      style={{ backgroundColor: "#160016" }}
    >
      {/* Full-screen particle effect background - z-0 to allow interaction */}
      <div className="fixed inset-0 z-0">
        <ThreeParticles />
      </div>

      {/* Content overlay - pointer-events-none on container, auto on card */}
      <div className="relative z-10 flex min-h-screen items-center justify-center p-4 pointer-events-none">
        <motion.div
          className="w-full max-w-md pointer-events-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Glass morphism card */}
          <div className="rounded-2xl border border-white/10 bg-black/40 p-8 backdrop-blur-xl shadow-2xl">
            {/* Logo and header */}
            <div className="mb-8 text-center">
              <motion.div
                className="mb-6 flex justify-center"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.5 }}
              >
                <Image
                  src={logoSrc}
                  alt="Nudj"
                  width={80}
                  height={80}
                  priority
                  className="h-20 w-20 object-contain"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <h1 className="text-3xl font-bold text-white">
                  {session?.data?.user ? "Welcome back" : "Welcome to Nudj"}
                </h1>
                <p className="mt-2 text-sm text-white/60">
                  {session?.data?.user
                    ? session.data.user.email
                    : "Build engaged communities with gamification"}
                </p>
              </motion.div>
            </div>

            {/* Main content */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              {session?.data?.user ? (
                // Logged in user - Organization selection
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label
                      htmlFor="organisation"
                      className="text-sm font-medium text-white/80"
                    >
                      Search & select organization
                    </Label>
                    <div className="relative">
                      {/* Search input that looks like the select trigger */}
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/60" />
                        <Input
                          placeholder="Search & select organization..."
                          value={
                            selectedOrg
                              ? organisations?.find(
                                  (o) => o.value === selectedOrg
                                )?.label || ""
                              : orgSearchQuery
                          }
                          onChange={(e) => {
                            setOrgSearchQuery(e.target.value);
                            setSelectedOrg("");
                            setIsSelectOpen(true);
                          }}
                          onFocus={() => {
                            setIsSelectOpen(true);
                            if (selectedOrg) {
                              // Clear selection when focusing to allow searching
                              setOrgSearchQuery(
                                organisations?.find(
                                  (o) => o.value === selectedOrg
                                )?.label || ""
                              );
                              setSelectedOrg("");
                            }
                          }}
                          disabled={
                            isLoading ||
                            !organisations ||
                            organisations.length === 0
                          }
                          className="h-12 pl-10 pr-10 border-white/10 bg-white/5 text-white placeholder:text-white/40 hover:bg-white/10 focus:bg-white/10 focus:ring-0"
                          autoComplete="off"
                        />
                        <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
                      </div>

                      {/* Dropdown results */}
                      {isSelectOpen && (
                        <div className="absolute top-full left-0 right-0 mt-1 z-50 max-h-[200px] overflow-auto rounded-md border border-white/10 bg-black/90 backdrop-blur-xl shadow-lg">
                          {filteredOrganisations.length > 0 ? (
                            filteredOrganisations.map((org) => (
                              <button
                                key={org.value}
                                type="button"
                                onClick={() => {
                                  setSelectedOrg(org.value);
                                  setOrgSearchQuery("");
                                  setIsSelectOpen(false);
                                }}
                                className="w-full px-3 py-2 text-left text-white/80 hover:bg-white/10 hover:text-white transition-colors"
                              >
                                {org.label}
                              </button>
                            ))
                          ) : (
                            <div className="py-6 text-center text-white/60">
                              <div className="flex flex-col items-center gap-2">
                                <Search className="h-4 w-4" />
                                <p className="text-sm">
                                  No organizations found
                                </p>
                                {orgSearchQuery && (
                                  <p className="text-xs text-white/40">
                                    Try a different search term
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Click outside to close */}
                      {isSelectOpen && (
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => setIsSelectOpen(false)}
                        />
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Button
                      onClick={handleContinue}
                      disabled={!selectedOrg}
                      className="h-12 w-full bg-primary hover:bg-primary/90"
                      size="lg"
                    >
                      Continue to Dashboard
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>

                    <div className="flex items-center gap-3 my-3">
                      <div className="flex-1 border-t border-white/10" />
                      <span className="text-xs text-white/40">or</span>
                      <div className="flex-1 border-t border-white/10" />
                    </div>

                    <Button
                      variant="outline"
                      onClick={() => router.push("/create-organization")}
                      className="h-11 w-full border-white/10 bg-white/5 text-white hover:bg-white/10"
                    >
                      <Sparkles className="mr-2 h-4 w-4" />
                      Create New Organization
                    </Button>

                    <Button
                      variant="ghost"
                      onClick={async () => {
                        await signOut({ callbackUrl: "/welcome" });
                      }}
                      className="h-10 w-full text-white/60 hover:bg-white/5 hover:text-white"
                    >
                      Sign Out
                    </Button>
                  </div>
                </div>
              ) : (
                // Not logged in - Sign in options
                <div className="space-y-6">
                  <AnimatePresence mode="wait">
                    <AuthScreen />
                  </AnimatePresence>
                </div>
              )}
            </motion.div>
          </div>

          {/* Scroll indicator for mobile */}
          <motion.div
            className="mt-8 flex justify-center md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            transition={{ delay: 1, duration: 1 }}
          >
            <ChevronDown className="h-6 w-6 animate-bounce text-white/40" />
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center bg-black">
          <div className="text-white">Loading...</div>
        </div>
      }
    >
      <WelcomePageContent />
    </Suspense>
  );
}
